import { CognitoUser } from '@aws-amplify/auth';
import privacyPolicyPath from 'assets/privacy_policy_dk.md';
import termsAndConditionsPath from 'assets/terms_and_conditions.md';
import { Auth } from 'aws-amplify';
import axios, { AxiosError } from 'axios';
import { makeObservable, observable, action, computed } from 'mobx';
import { NavigateFunction } from 'react-router';
import config from 'utilities/config';
import ProfileService from '../services/profileService';
import { put } from './../common/network';
import { IBidderDto } from './../models/dtos/IBidderDto';
import { IStore } from './index';
import { AcceptTermsPhase } from './models/AcceptTermsPhase';
import { CompleteLogonPhase } from './models/CompleteLogonPhase';
import { ILoginPhase } from './models/ILoginPhase';
import { LogonResult } from './models/LogonResult';
import { UpdateProfilePhase } from './models/UpdateProfilePhase';
import { VerbatimLogonResult } from './models/VerbatimLogonResult';

const CURRENT_TERMS_VERSION = 2;

export class UserStore {
  constructor(private rootStore: IStore) {
    makeObservable(this);

    this.setAuthenticationHeader();
  }

  @observable user?: CognitoUser = undefined;
  @observable profile?: IBidderDto = undefined;

  @observable forgotPasswordData?: any = undefined;

  // Set when Cognito returns a NEW_PASSWORD_REQUIRED challenge. Stored on the
  // store (rather than passed through react-router navigation state) because
  // the CognitoUser instance does not survive a route change reliably.
  @observable pendingNewPasswordUser?: CognitoUser | any = undefined;

  // Surfaced when an automatic session restore (app boot) or an onboarding
  // phase fails so the UI can show something instead of silently leaving
  // the user logged out with no feedback.
  @observable sessionRestoreError?: string = undefined;

  logonPhases: ILoginPhase[] = [];

  /**
   * Attempts to resume an existing Cognito session on app start. Unlike the
   * previous implementation, this takes a real `NavigateFunction` so that
   * any pending onboarding phases (accept terms / update profile) actually
   * redirect the user instead of silently doing nothing.
   */
  @action public async initializeSession(navigate: NavigateFunction) {
    try {
      const authenticatedUser = await Auth.currentAuthenticatedUser(undefined);
      const result = await this.completeLogon(authenticatedUser, navigate);
      if (
        result.status !== LogonResult.success &&
        result.status !== LogonResult.acceptTerms
      ) {
        this.sessionRestoreError = result.message;
      }
    } catch (ex) {
      // No authenticated user / session could not be restored. This is the
      // normal case for a logged out visitor, so we don't surface an error.
    }
  }

  @computed get isAuthenticated() {
    return !!this.user;
  }

  @action public async logout() {
    await Auth.signOut();
    this.user = undefined;
    delete axios.defaults.headers.common['Authorization'];
  }

  async setAuthenticationHeader() {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      if (accessToken) {
        axios.defaults.headers.common['Authorization'] = accessToken;
      } else {
        return this.logout();
      }
    } catch (x) {
      delete axios.defaults.headers.common['Authorization'];
    }
  }

  public async login(
    username: string,
    password: string,
    navigate: NavigateFunction
  ): Promise<VerbatimLogonResult> {
    var user: CognitoUser | any;

    // Trim stray whitespace (very common with copy-pasted emails / mobile
    // keyboards) so we don't fail sign-in on an otherwise valid account.
    const trimmedUsername = username.trim();

    try {
      user = await Auth.signIn({
        username: trimmedUsername,
        password,
      });
      console.log('user :>> ', user);
    } catch (err: any) {
      console.log('err :>> ', err);
      if (err.code === 'UserNotConfirmedException') {
        // The error happens if the user didn't finish the confirmation step when signing up
        // In this case you need to resend the code and confirm the user
        // About how to resend the code and confirm the user, please check the signUp part
        return new VerbatimLogonResult(
          LogonResult.requiresConfirmation,
          'Du skal bekræfte din konto'
        );
      } else if (err.code === 'PasswordResetRequiredException') {
        // The error happens when the password is reset in the Cognito console
        // In this case you need to call forgotPassword to reset the password
        return new VerbatimLogonResult(
          LogonResult.passwordResetRequired,
          'Du mangler at nulstille dit kodeord'
        );
      } else if (err.code === 'NotAuthorizedException') {
        // The error happens when the incorrect password is provided
        return new VerbatimLogonResult(
          LogonResult.wrongPassword,
          'Du har indtastet et forkert kodeord'
        );
      } else if (err.code === 'UserNotFoundException') {
        // The error happens when the supplied username/email does not exist in the Cognito user pool
        return new VerbatimLogonResult(
          LogonResult.userDoesNotExist,
          'Kunne ikke finde din bruger'
        );
      } else if (err.code === 'InvalidParameterException') {
        // The error happens when a user has been created directly in the Cognito web interface using import users feature
        return new VerbatimLogonResult(
          LogonResult.passwordResetRequired,
          'Du skal nulstille din email'
        );
      } else {
        return new VerbatimLogonResult(
          LogonResult.failure,
          'Vi kunne desværre ikke logge Dem ind. Er De sikker på at de har indtastet det rigtige brugernavn og kodeord.'
        );
      }
    }

    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      // Store the CognitoUser on the store itself rather than relying on it
      // being carried through react-router navigation state, which does not
      // reliably survive the route change.
      this.pendingNewPasswordUser = user;
      return new VerbatimLogonResult(LogonResult.newPassword, '', user);
    } else {
      try {
        return await this.completeLogon(user, navigate);
      } catch (ex) {
        console.error(ex);
        return new VerbatimLogonResult(
          LogonResult.failure,
          'Vi kunne desværre ikke logge Dem ind. Der ser ud til at være en teknisk fejl.'
        );
      }
    }
  }

  async completeNewPassword(
    password: string,
    requiredAttributes: any,
    navigate: NavigateFunction
  ) {
    const newUser = await Auth.completeNewPassword(
      this.pendingNewPasswordUser ?? this.user,
      password,
      requiredAttributes
    );
    this.pendingNewPasswordUser = undefined;
    return this.completeLogon(newUser, navigate);
  }

  /**
   * Builds the onboarding phases (accept terms / update profile) that need
   * to run before a login can be considered complete.
   */
  private buildOnboardingPhases(
    user: CognitoUser | any,
    navigate: NavigateFunction
  ): ILoginPhase[] {
    return [
      new AcceptTermsPhase(termsAndConditionsPath),
      new AcceptTermsPhase(privacyPolicyPath),
      new UpdateProfilePhase(),
      new CompleteLogonPhase(async () => {
        try {
          await put(config().services.user, 'acceptTerms', {
            acceptedTermsVersion: CURRENT_TERMS_VERSION,
          });
          this.user = user;
          this.sessionRestoreError = undefined;
          navigate('/');
        } catch (ex: any) {
          console.error(ex);
          // Previously this failure was only logged, leaving the user stuck
          // on the onboarding page with no feedback and never logged in.
          // Surface an error and send them back to a known-good page.
          this.sessionRestoreError =
            'Vi kunne desværre ikke gemme dine oplysninger. Prøv at logge på igen.';
          navigate('/logon');
        }
      }),
    ];
  }

  private async startOnboarding(
    user: CognitoUser | any,
    navigate: NavigateFunction
  ): Promise<VerbatimLogonResult> {
    const phases = this.buildOnboardingPhases(user, navigate);
    this.logonPhases = phases;
    const firstPhase = this.logonPhases.shift()!;
    firstPhase.perform(navigate);
    return new VerbatimLogonResult(LogonResult.acceptTerms, '');
  }

  private async completeLogon(
    user: CognitoUser | any,
    navigate: NavigateFunction
  ): Promise<VerbatimLogonResult> {
    let profile: IBidderDto | undefined = undefined;

    try {
      profile = await new ProfileService().current();
    } catch (error) {
      console.warn('failed to login');
      const axiosError = error as AxiosError;
      console.log(axiosError);
      if (axiosError.response && axiosError.response.status === 404) {
        return this.startOnboarding(user, navigate);
      }

      throw error;
    }

    this.profile = profile;

    if (profile === undefined) {
      return new VerbatimLogonResult(
        LogonResult.failure,
        'Kunne ikke finde bruger (code: 1)'
      );
    }

    if (profile.onlineUser === undefined) {
      return new VerbatimLogonResult(
        LogonResult.failure,
        'Brugeren har desværre ingen online profil (code: 2)'
      );
    }

    if (
      profile.onlineUser.acceptedTermsVersion === undefined ||
      profile.onlineUser.acceptedTermsVersion < CURRENT_TERMS_VERSION
    ) {
      console.log('no accepted terms version');
      return this.startOnboarding(user, navigate);
    }

    console.log('terms accepted');

    this.user = user;
    this.sessionRestoreError = undefined;
    return new VerbatimLogonResult(LogonResult.success, '');
  }

  nextLogonPhase(navigate: NavigateFunction) {
    const nextPhase = this.logonPhases.shift();
    if (!nextPhase) {
      // The phase queue is empty, most likely because it was lost on a page
      // refresh. Rather than doing nothing (which left users stuck on the
      // onboarding page with no way forward), send them somewhere sane so
      // they can retry the login and rebuild the phase queue.
      navigate(this.isAuthenticated ? '/' : '/logon');
      return false;
    }

    nextPhase.perform(navigate);
    return true;
  }
}
